import { useParams } from 'react-router'
import authStore from '../../../../store/authStore'
import { useCallback, useEffect, useState } from 'react'
import { FetchStatus } from '../../../../types/fetch-status.types'
import constants from '../../../../constants'
import { MedicalReimbursement } from '../../../../database/tables'
import MedicalReimbursementService from '../../../../services/medical-reimbursement'
import PERMISSIONS from '../../../../private/permissions'
import { ReimbursementType } from '../../../../database/enums/ReimbursementType.enum'
import { Currency } from '../../../../database/enums/Currency.enum'
import { useForm } from 'react-hook-form'
import medicalReimbursementEditSchema, {
  MedicalReimbursementEditSchema,
} from '../../../../database/validations/MedicalReimbursementEdit'
import { zodResolver } from '@hookform/resolvers/zod'
import toast from 'react-hot-toast'
import { ApprovalStatus } from '../../../../database/enums/ApprovalStatus.enum'

const formEmptyValue: MedicalReimbursementEditSchema = {
  name: '',
  email: '',
  serviceDate: new Date(),
  reimbursementType: ReimbursementType.CONSULTATION,
  vendor: '',
  description: '',
  totalAmountClaimed: {
    currency: Currency.IDR,
    value: '',
  },
}

export default function useAdminMedicalReimbursementEditViewModel() {
  const params = useParams()
  const id = params.id as string | null
  const user = authStore(state => state.user)

  const [dataUpdateStatus, setDataUpdateStatus] = useState<FetchStatus>(constants.fetchStatus.default)
  const [dataFetchStatus, setDataFetchStatus] = useState<FetchStatus>(constants.fetchStatus.default)
  const [detailData, setDetailData] = useState<MedicalReimbursement | null>(null)

  const canEdit = useCallback(() => {
    if (!!user && !!user.email) return PERMISSIONS.getEditReimbursementEmails().includes(user.email)
    return false
  }, [user])()

  const formHook = useForm<MedicalReimbursementEditSchema>({
    defaultValues: formEmptyValue,
    mode: 'onChange',
    resolver: zodResolver(medicalReimbursementEditSchema),
  })

  async function fetchDetailData() {
    if (!!id) {
      setDataFetchStatus({ ...constants.fetchStatus.default, isLoading: true })
      await MedicalReimbursementService.getById({ id })
        .then(res => {
          setDataFetchStatus({ ...constants.fetchStatus.default, isSuccess: true })
          setDetailData(res)
          formHook.reset({
            ...formEmptyValue,
            name: res.name,
            email: res.email,
            serviceDate: res.serviceDate,
            reimbursementType: res.reimbursementType,
            vendor: res.vendor,
            description: res.description,
            totalAmountClaimed: {
              currency: res.totalAmountClaimed.currency,
              value: String(res.totalAmountClaimed.value),
            },
          })
        })
        .catch(error => {
          console.log(error)
          setDataFetchStatus({ ...constants.fetchStatus.default, isError: true })
        })
    }
  }

  async function onSubmit(data: MedicalReimbursementEditSchema) {
    if (canEdit) {
      // TODO: remove comment if edit restriction is needed
      // if (detailData?.approvalStatus === ApprovalStatus.UNRESPONDED) {
      if (!!id) {
        setDataUpdateStatus({ ...constants.fetchStatus.default, isLoading: true })
        await MedicalReimbursementService.update({
          id,
          data: {
            name: data.name,
            email: data.email,
            serviceDate: data.serviceDate,
            reimbursementType: data.reimbursementType as ReimbursementType | undefined,
            vendor: data.vendor,
            description: data.description,
            totalAmountClaimed: {
              currency: (data.totalAmountClaimed?.currency ?? Currency.IDR) as Currency,
              value: !!data.totalAmountClaimed?.value ? Number(data.totalAmountClaimed.value) : 0,
            },
          },
          updatedBy: user?.displayName ?? user?.email ?? user?.uid ?? null,
        })
          .then(() => {
            setDataUpdateStatus({ ...constants.fetchStatus.default, isSuccess: true })
            toast.success('Update succeed!')
            fetchDetailData()
          })
          .catch(error => {
            console.log(error)
            setDataUpdateStatus({ ...constants.fetchStatus.default, isError: true })
            toast.error('Update failed!')
          })
      }
      // } else toast.error('This document is not editable anymore!')
    } else toast.error("You don't have permission to do this action!")
  }

  useEffect(() => {
    fetchDetailData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  function resetPage() {
    if (detailData) {
      formHook.reset({
        ...formEmptyValue,
        name: detailData.name,
        email: detailData.email,
        serviceDate: detailData.serviceDate,
        reimbursementType: detailData.reimbursementType,
        vendor: detailData.vendor,
        description: detailData.description,
        totalAmountClaimed: {
          currency: detailData.totalAmountClaimed.currency,
          value: String(detailData.totalAmountClaimed.value),
        },
      })
      setDataUpdateStatus(constants.fetchStatus.default)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  return {
    id,
    detailData,
    dataFetchStatus,
    dataUpdateStatus,
    onSubmit,
    resetPage,
    formHook,
  }
}
